import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';

import Card from 'components/card/Card';
import ErrorState from 'components/states/ErrorState';
import Loader from 'components/Loader';
import { translate } from '../../../i18n';
import { AdminNotificationDto } from '../../../models/notifications/dto/admin-notification.dto';
import { QueuedNotificationType } from '../../../models/notification-queue-consumer/dto/queued-notification.entity';
import { tableHeaderTextProps, tableProps, tableRowProps, tableCellProps, tableThProps, tableThFlexProps } from '../../../theme/constants';
import { typography } from 'theme/tokens';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import { useGetNotifications } from '../hooks/useGetNotifications';
import { getNotificationTypeColor } from '../utils/notification.utils';

const columnHelper = createColumnHelper<AdminNotificationDto>();

const getTypeLabel = (type: string): string =>
  translate(`notification.types.${type}` as any) || type;

export default function NotificationListTable() {
  const [currentPage, setCurrentPage] = useState(0);
  const [typeFilter, setTypeFilter] = useState<QueuedNotificationType | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedNotification, setSelectedNotification] = useState<AdminNotificationDto | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const limit = 20;
  const { selectedOrganization } = useSelectedOrganization();

  const { data, isFetching, isInitialLoading, isError } = useGetNotifications({
    page: currentPage,
    limit,
    organizationId: selectedOrganization?.id,
    type: typeFilter || undefined,
    search: debouncedSearch || undefined,
  });

  /**
   * The read's own four states (D62), replacing the `!selectedOrganization`
   * early return this table used to open with.
   *
   * That guard was correct and completely re-derived the hook's single
   * `enabled: !!organizationId`, so the never-issued case reached a prompt
   * rather than the permanent spinner the raw `isLoading` would have produced.
   * It is converted for D62's reason — the gate is in
   * `useGetNotifications.ts`, the guard was here, and a second `enabled:` term
   * added later would have silently stopped being covered.
   *
   * Two live halves come with it. The error block keyed off the raw `error`, so
   * a background refetch that failed put an error card under rows that were
   * still good; `unreadable` requires `!data` (R96-3). And the empty claim
   * ("No notifications") now needs `answered` rather than the absence of three
   * flags, so it can only follow a read that actually returned.
   */
  const readState = classifyRead(isError, isInitialLoading, data);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setCurrentPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset page when org or type changes
  useEffect(() => {
    setCurrentPage(0);
  }, [selectedOrganization?.id, typeFilter]);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.400');

  const columns = useMemo(() => [
    columnHelper.accessor('createdAt', {
      id: 'createdAt',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('notification.date' as any)}
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="500">
          {new Date(info.getValue()).toLocaleString()}
        </Text>
      ),
    }),
    columnHelper.accessor('receiverName', {
      id: 'receiverName',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('notification.recipient' as any)}
        </Text>
      ),
      cell: (info) => (
        <Box>
          <Text color={textColor} fontSize="sm" fontWeight="600">
            {info.getValue()}
          </Text>
          <Text color="gray.500" fontSize="xs">
            {info.row.original.receiverEmail}
          </Text>
        </Box>
      ),
    }),
    columnHelper.accessor('type', {
      id: 'type',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('notification.type' as any)}
        </Text>
      ),
      cell: (info) => {
        const type = info.getValue();
        if (!type) {
          return (
            <Badge colorScheme="gray" fontSize="xs">
              —
            </Badge>
          );
        }
        return (
          <Badge colorScheme={getNotificationTypeColor(type)} fontSize="xs">
            {getTypeLabel(type)}
          </Badge>
        );
      },
    }),
    columnHelper.accessor('message', {
      id: 'message',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('notification.message' as any)}
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="400" noOfLines={2} maxW="400px">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('isRead', {
      id: 'isRead',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('notification.status' as any)}
        </Text>
      ),
      cell: (info) => (
        <Badge colorScheme={info.getValue() ? 'green' : 'orange'} fontSize="xs">
          {info.getValue()
            ? translate('notification.read' as any)
            : translate('notification.unread' as any)}
        </Badge>
      ),
    }),
  ], [textColor]);

  const table = useReactTable({
    data: data?.notifications || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (data?.pagination.hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleRowClick = (notification: AdminNotificationDto) => {
    setSelectedNotification(notification);
    onOpen();
  };

  if (readState === 'never-issued') {
    return (
      <Card flexDirection="column" w="100%" px="0px">
        <ScopePrompt gate="organization" />
      </Card>
    );
  }

  return (
    <>
      <Card flexDirection="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
        <Flex px="25px" mb="8px" justifyContent="space-between" align="center">
          <Box>
            <Text color={textColor} {...typography.pageTitle}>
              {translate('notification.title' as any)}
            </Text>
            <Text color="gray.500" fontSize="sm">
              {translate('notification.subtitle' as any)}
            </Text>
          </Box>

          {data?.pagination && (
            <HStack spacing={4}>
              <Text fontSize="sm" color={secondaryTextColor}>
                {translate('game.page_of')} {currentPage + 1} {translate('game.of')}{' '}
                {data.pagination.totalPages}
                {data.pagination.totalCount > 0 &&
                  ` (${data.pagination.totalCount} ${translate('game.total')})`}
              </Text>
              <HStack spacing={2}>
                <Button
                  size="sm"
                  onClick={handlePreviousPage}
                  isDisabled={currentPage === 0 || isFetching}
                  variant="outline"
                >
                  {translate('game.previous')}
                </Button>
                <Button
                  size="sm"
                  onClick={handleNextPage}
                  isDisabled={!data.pagination.hasNextPage || isFetching}
                  variant="outline"
                >
                  {translate('game.next')}
                </Button>
              </HStack>
            </HStack>
          )}
        </Flex>

        {/* Filters */}
        <Flex px="25px" mb="16px" gap={4} flexWrap="wrap">
          <Select
            size="sm"
            maxW="220px"
            placeholder={translate('notification.all_types' as any)}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as QueuedNotificationType | '')}
          >
            {Object.values(QueuedNotificationType).map((t) => (
              <option key={t} value={t}>
                {getTypeLabel(t)}
              </option>
            ))}
          </Select>
          <Input
            size="sm"
            maxW="260px"
            placeholder={translate('notification.search_placeholder' as any)}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </Flex>

        <Box>
          <Table {...tableProps}>
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th key={header.id} colSpan={header.colSpan} {...tableThProps} borderColor={borderColor}>
                      <Flex {...tableThFlexProps}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => (
                <Tr
                  key={row.id}
                  {...tableRowProps}
                  onClick={() => handleRowClick(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      key={cell.id}
                      {...tableCellProps}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>

          {(readState === 'loading' || isFetching) && <Loader isLoading={true} variant="spinner" />}

          {readState === 'unreadable' && (
            <ErrorState message={translate('notification.error_loading' as any)} />
          )}

          {readState === 'answered' && !isFetching && (!data?.notifications || data.notifications.length === 0) && (
            <Box textAlign="center" py={8}>
              <Text color="gray.500" fontSize="lg">
                {translate('notification.no_notifications' as any)}
              </Text>
            </Box>
          )}
        </Box>
      </Card>

      {/* Detail Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{translate('notification.details' as any)}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedNotification && (
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color="gray.500">
                    {translate('notification.recipient' as any)}
                  </Text>
                  <Text fontSize="md">{selectedNotification.receiverName}</Text>
                  <Text fontSize="sm" color={secondaryTextColor}>
                    {selectedNotification.receiverEmail}
                  </Text>
                </Box>

                {selectedNotification.triggerName && (
                  <Box>
                    <Text fontWeight="bold" fontSize="sm" color="gray.500">
                      {translate('notification.triggered_by' as any)}
                    </Text>
                    <Text fontSize="md">{selectedNotification.triggerName}</Text>
                  </Box>
                )}

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color="gray.500">
                    {translate('notification.type' as any)}
                  </Text>
                  {selectedNotification.type ? (
                    <Badge colorScheme={getNotificationTypeColor(selectedNotification.type)}>
                      {getTypeLabel(selectedNotification.type)}
                    </Badge>
                  ) : (
                    <Text fontSize="md" color="gray.400">—</Text>
                  )}
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color="gray.500">
                    {translate('notification.message' as any)}
                  </Text>
                  <Text fontSize="md" whiteSpace="pre-wrap">
                    {selectedNotification.message}
                  </Text>
                </Box>

                {selectedNotification.url && (
                  <Box>
                    <Text fontWeight="bold" fontSize="sm" color="gray.500">
                      {translate('notification.url' as any)}
                    </Text>
                    <Text fontSize="sm" color="brand.500">
                      {selectedNotification.url}
                    </Text>
                  </Box>
                )}

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color="gray.500">
                    {translate('notification.status' as any)}
                  </Text>
                  <Badge colorScheme={selectedNotification.isRead ? 'green' : 'orange'}>
                    {selectedNotification.isRead
                      ? translate('notification.read' as any)
                      : translate('notification.unread' as any)}
                  </Badge>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color="gray.500">
                    {translate('notification.date' as any)}
                  </Text>
                  <Text fontSize="md">
                    {new Date(selectedNotification.createdAt).toLocaleString()}
                  </Text>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
